import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "../../store/RootStore";
import { getApiAsync } from "../../api";

export type SimMode = "auto" | "manual";
export type FaultType = "recoverable" | "intervention";

/** Mapeo de estado real → hito del roadmap (los transitorios no son nodos). */
const DNDA_MILESTONE: Record<string, string> = {
  contratosFirmados: "contratosFirmados",
  generandoEjemplar: "contratosFirmados",
  ejemplarGenerado: "ejemplarGenerado",
  registrandoDnda: "ejemplarGenerado",
  dndaRegistrado: "dndaRegistrado",
  ejemplarHabilitadoUpload: "ejemplarHabilitadoUpload",
  subiendoEjemplar: "ejemplarHabilitadoUpload",
  ejemplarSubido: "ejemplarSubido",
  comprobanteRecibido: "comprobanteRecibido",
};

const SADAIC_MILESTONE: Record<string, string> = {
  contratosFirmados: "contratosFirmados",
  subiendoSadaic: "contratosFirmados",
  sadaicEnTramite: "sadaicEnTramite",
  sadaicPublicado: "sadaicPublicado",
};

/** Gates conocidos del circuito DNDA→SADAIC (orden de ejecución). */
export const SIM_GATES: { key: string; label: string }[] = [
  { key: "dnda-registro", label: "Registro DNDA" },
  { key: "dnda-email", label: "Habilitación (email DNDA)" },
  { key: "dnda-upload-ejemplar", label: "Subida del ejemplar al portal DNDA" },
  { key: "ftp-upload", label: "Subida FTP SADAIC" },
  { key: "send-email", label: "Aviso email a SADAIC" },
];

/** Config por gate del scenario: 'advance' (default) o un fault. */
export type GatePlan = "advance" | FaultType;

export class SimulationControlStore {
  editions: any[] = [];
  selectedEditionId: string | null = null;
  mode: SimMode = "manual";
  /** Por gate: 'advance' | 'recoverable' | 'intervention'. */
  plan: Record<string, GatePlan> = {};

  session: any = null;
  botRuns: any[] = [];
  workerEvents: any[] = [];
  dndaState: string | null = null;
  sadaicState: string | null = null;
  editionName: string | null = null;

  loading = false;
  starting = false;
  error: string | null = null;
  /** Últimos eventos SSE de simulación (con hora de recepción, para el timeline). */
  events: { label: string; at: Date }[] = [];
  private refreshTimer: any = null;

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this, { rootStore: false, refreshTimer: false } as any);
  }

  /** Refresh con debounce: coalesce ráfagas (SSE + poll) en un solo refresh. */
  scheduleRefresh(delay = 1200) {
    if (this.refreshTimer) return;
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      void this.refresh();
    }, delay);
  }

  private async api() {
    return getApiAsync(this.rootStore.authStore as any, (this.rootStore as any).activityStore);
  }

  setMode(m: SimMode) {
    this.mode = m;
  }

  setPlan(gate: string, plan: GatePlan) {
    this.plan = { ...this.plan, [gate]: plan };
  }

  selectEdition(id: string | null) {
    this.selectedEditionId = id;
    const ed = this.editions.find((e: any) => e.id === id);
    this.editionName = ed?.data_?.name ?? null;
  }

  reset() {
    this.session = null;
    this.selectedEditionId = null;
    this.botRuns = [];
    this.workerEvents = [];
    this.dndaState = null;
    this.sadaicState = null;
    this.editionName = null;
    this.events = [];
  }

  /** Estado de actividad derivado, para mostrar qué está pasando. */
  get activity(): string {
    if (!this.session) return "";
    if (this.session.status === "waiting") {
      return `⏸ en espera — gate ${this.currentGate ?? "?"}`;
    }
    const circuit = (this.workerEvents ?? []).filter(
      (w) => typeof w.jobType === "string" && (w.jobType.includes("Dnda") || w.jobType.includes("Sadaic") || w.jobType.includes("Ejemplar"))
    );
    if (circuit.some((w) => w.status === "dead_letter")) return "✕ falló (dead_letter) — revisá WorkerEvents";
    if (circuit.some((w) => w.status === "processing" || w.status === "queued")) return "⚙ procesando…";
    if (circuit.some((w) => (w.retryCount ?? 0) > 0)) return "↻ reintentando…";
    return this.session.status ?? "";
  }

  /** ¿Tiene sentido pollear? Solo sesiones activas/en progreso (no terminal ni
   * atascadas en dead_letter). Evita saturar el rate-limiter del server. */
  get shouldPoll(): boolean {
    if (!this.session) return false;
    const st = this.session.status;
    if (st !== "running" && st !== "waiting") return false;
    const stuck = (this.workerEvents ?? []).some(
      (w) =>
        w.status === "dead_letter" &&
        typeof w.jobType === "string" &&
        (w.jobType.includes("Dnda") || w.jobType.includes("Sadaic") || w.jobType.includes("Ejemplar"))
    );
    return !stuck;
  }

  get currentGate(): string | null {
    return (this.session as any)?.currentGate ?? null;
  }

  get isWaiting(): boolean {
    return this.session?.status === "waiting";
  }

  /** Hito del roadmap (mapea estados transitorios al milestone correspondiente). */
  get dndaMilestone(): string | null {
    return this.dndaState ? DNDA_MILESTONE[this.dndaState] ?? this.dndaState : null;
  }

  get sadaicMilestone(): string | null {
    return this.sadaicState ? SADAIC_MILESTONE[this.sadaicState] ?? this.sadaicState : null;
  }

  /** Scenario JSON para el server: { [gate]: { fail?: 'recoverable'|'intervention' } }. */
  private buildScenario(): Record<string, { fail?: FaultType }> {
    const scenario: Record<string, { fail?: FaultType }> = {};
    for (const { key } of SIM_GATES) {
      const p = this.plan[key];
      if (p === "recoverable" || p === "intervention") scenario[key] = { fail: p };
    }
    return scenario;
  }

  /** Al cargar el panel: retoma la sesión activa (running/waiting). Si se pasa
   * `forEditionId` (navegación contextual, p.ej. desde /automation), retoma SOLO
   * la sesión de ESA edición; si no tiene, queda en el setup con la edición
   * preseleccionada. */
  async loadActiveSessions(forEditionId?: string) {
    try {
      const api = await this.api();
      const res = await api.simActiveSessions({
        where: {
          status: { in: ["running", "waiting"] },
          relatedEntityType: { equals: "SignedEdition" },
          ...(forEditionId ? { relatedEntityId: { equals: forEditionId } } : {}),
        } as any,
        orderBy: [{ startedAt: "desc" }] as any,
        offset: 0,
        take: 1,
      } as any);
      const sessions = ((res as any).simulationSessionListWithOffset ?? []) as any[];
      if (sessions.length > 0) {
        const sess = sessions[0];
        runInAction(() => {
          this.session = sess;
          this.selectedEditionId = (sess as any).relatedEntityId ?? null;
        });
        await this.refresh(sess.id);
      } else if (forEditionId) {
        // Sin sesión activa para esa edición: setup con la edición preseleccionada.
        runInAction(() => {
          this.showAllEditions = true;
          this.selectedEditionId = forEditionId;
        });
        await this.loadEditions();
        runInAction(() => {
          const ed = this.editions.find((e: any) => e.id === forEditionId);
          this.editionName = ed?.data_?.name ?? null;
        });
      }
    } catch (e: any) {
      runInAction(() => (this.error = e?.message ?? "Error al cargar sesiones activas"));
    }
  }

  /** Si true, el picker muestra TODAS las ediciones (continuar desde cualquier
   * estado / resetear); si false, solo las del inicio del circuito. */
  showAllEditions = false;

  setShowAllEditions(v: boolean) {
    this.showAllEditions = v;
    void this.loadEditions();
  }

  /** Reset al estado inicial del circuito (aborta sesiones + resetea la entidad). */
  async resetEdition(id: string) {
    this.error = null;
    try {
      const api = await this.api();
      await (api as any).simulationReset({ relatedEntityType: "SignedEdition", relatedEntityId: id });
      if (this.session?.relatedEntityId === id) this.reset();
      await this.loadEditions();
    } catch (e: any) {
      runInAction(() => (this.error = e?.message ?? "Error al resetear"));
    }
  }

  async loadEditions() {
    this.loading = true;
    this.error = null;
    try {
      const api = await this.api();
      const res = await api.simEditions({
        where: this.showAllEditions
          ? undefined
          : ({ dndaState: { id: { equals: "contratosFirmados" } } } as any),
        offset: 0,
        take: 100,
      });
      runInAction(() => {
        this.editions = ((res as any).signedEditionListWithOffset ?? []) as any[];
      });
    } catch (e: any) {
      runInAction(() => (this.error = e?.message ?? "Error al cargar ediciones"));
    } finally {
      runInAction(() => (this.loading = false));
    }
  }

  async start() {
    if (!this.selectedEditionId) return;
    this.starting = true;
    this.error = null;
    try {
      const api = await this.api();
      const res = await api.simulationStart({
        relatedEntityType: "SignedEdition",
        relatedEntityId: this.selectedEditionId,
        mode: this.mode,
        scenario: this.buildScenario(),
        label: `Sim ${this.mode}`,
      });
      const sessionId = res.simulationStart.sessionId;
      await this.refresh(sessionId);
    } catch (e: any) {
      runInAction(() => (this.error = e?.message ?? "Error al iniciar la simulación"));
    } finally {
      runInAction(() => (this.starting = false));
    }
  }

  async control(gate: string | null, action: "advance" | "fail" | "abort", faultType?: FaultType) {
    if (!this.session) return;
    this.error = null;
    try {
      const api = await this.api();
      await api.simulationControl({
        sessionId: this.session.id,
        gate: gate ?? undefined,
        action,
        faultType: faultType ?? undefined,
      });
      this.scheduleRefresh();
    } catch (e: any) {
      runInAction(() => (this.error = e?.message ?? "Error en el control de simulación"));
    }
  }

  /** Aborta la sesión activa (aunque esté running/trabada) y vuelve al setup. */
  async abort() {
    await this.control(null, "abort");
    this.reset();
    await this.loadEditions();
  }

  /** Refresca la sesión activa + los BotRuns de la edición. */
  async refresh(sessionId?: string) {
    const id = sessionId ?? this.session?.id;
    if (!id) return;
    try {
      const api = await this.api();
      const sres = await api.simSession({ id });
      runInAction(() => {
        this.session = (sres as any).simulationSessionOne;
      });
      if (this.selectedEditionId) {
        const [bres, eres, wres] = await Promise.all([
          api.simBotRuns({
            where: { relatedEntityId: { equals: this.selectedEditionId } } as any,
            offset: 0,
            take: 50,
          }),
          api.simEditionState({ id: this.selectedEditionId }),
          api.simWorkerEvents({
            orderBy: [{ enqueuedAt: "desc" }] as any,
            offset: 0,
            take: 25,
          } as any),
        ]);
        runInAction(() => {
          this.botRuns = ((bres as any).botRunListWithOffset ?? []) as any[];
          const se = (eres as any).signedEditionOne;
          // dndaState/sadaicState son relaciones (objeto {id,...}).
          this.dndaState = se?.dndaState?.id ?? null;
          this.sadaicState = se?.sadaicState?.id ?? null;
          if (se?.data_?.name) this.editionName = se.data_.name;
          // WorkerEvents de ESTA edición (filtro client-side por el id anidado en payload).
          const all = ((wres as any).workerEventListWithOffset ?? []) as any[];
          this.workerEvents = all.filter(
            (w) => (w.payload as any)?.payload?.signedEditionId === this.selectedEditionId
          );
        });
      }
    } catch (e: any) {
      runInAction(() => (this.error = e?.message ?? "Error al refrescar"));
    }
  }


  /** Llamado por los handlers SSE de simulación: registra el evento y refresca. */
  onSimEvent(type: string, payload: any) {
    runInAction(() => {
      this.events = [
        { label: `${type}${payload?.gate ? ` · ${payload.gate}` : ""}`, at: new Date() },
        ...this.events,
      ].slice(0, 20);
    });
    // refresco best-effort con debounce (la fuente de verdad es la sesión persistida)
    this.scheduleRefresh();
  }
}
